import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField,
} from '@mui/material';
import type { ISelectData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ISelectComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

export interface IETTSelectComponentProps extends ITemplateComponentProps {
    value?: ISelectData;
}

export const ETTSelectComponent: React.FC<IETTSelectComponentProps> = (props) => {
    const { component, value } = props;
    const properties = component.properties as ISelectComponentProperties;
    const options = properties.options || [];
    const multiple = properties.multiple || false;
    const { isReadOnlyForCurrentUser } = useEmployeeTalk();

    const defaultValue = multiple
        ? (properties.defaultValue as string[]) || []
        : (properties.defaultValue as string) || '';

    const [selectedOptions, setSelectedOptions] = React.useState<string | string[]>(
        value?.selectedValue || defaultValue,
    );
    const [newOption, setNewOption] = React.useState('');

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    // Aktualisiere den State, wenn sich der Wert von außen ändert
    useEffect(() => {
        if (value?.selectedValue !== undefined) {
            setSelectedOptions(value.selectedValue);
        }
    }, [value]);

    const handleChange = (event: SelectChangeEvent<string | string[]>) => {
        const newValue = event.target.value;
        setSelectedOptions(newValue);
        if (props.onChange) {
            props.onChange({
                selectedValue: newValue,
                lastModified: new Date().toISOString(),
            });
        }
    };

    const handleAddOption = () => {
        if (newOption.trim() && !options.includes(newOption.trim())) {
            const updatedOptions = [...options, newOption.trim()];
            if (props.onEdit) {
                props.onEdit({
                    ...component,
                    properties: {
                        ...properties,
                        options: updatedOptions,
                    } as ISelectComponentProperties,
                });
            }
            setNewOption('');
        }
    };

    const handleRemoveOption = (index: number) => {
        const updatedOptions = [...options];
        updatedOptions.splice(index, 1);
        if (props.onEdit) {
            props.onEdit({
                ...component,
                properties: {
                    ...properties,
                    options: updatedOptions,
                } as ISelectComponentProperties,
            });
        }
    };

    const handleToggleMultiple = () => {
        if (props.onEdit) {
            props.onEdit({
                ...component,
                properties: {
                    ...properties,
                    multiple: !multiple,
                } as ISelectComponentProperties,
            });
            setSelectedOptions(multiple ? '' : []);
        }
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={() => (
                <Box>
                    <Box display="flex" alignItems="center" mb={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={langStrings.addOption}
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddOption();
                                }
                            }}
                        />
                        <IconButton color="primary" onClick={handleAddOption}>
                            <AddIcon />
                        </IconButton>
                    </Box>

                    <Box mb={2}>
                        {options.map((option, index) => (
                            <Chip
                                key={index}
                                label={option}
                                onDelete={() => handleRemoveOption(index)}
                                sx={{ m: 0.5 }}
                            />
                        ))}
                    </Box>

                    <FormControl>
                        <InputLabel id="multiple-select-label">{langStrings.multipleSelection}</InputLabel>
                        <Select
                            labelId="multiple-select-label"
                            value={multiple ? 'yes' : 'no'}
                            label={langStrings.multipleSelection}
                            onChange={handleToggleMultiple}
                        >
                            <MenuItem value="no">{langStrings.no}</MenuItem>
                            <MenuItem value="yes">{langStrings.yes}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}
            renderViewContent={() => (
                <FormControl fullWidth>
                    <Select
                        multiple={multiple}
                        value={selectedOptions}
                        onChange={handleChange}
                        disabled={isReadOnlyForCurrentUser}
                        renderValue={
                            multiple
                                ? (selected) => (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {(selected as string[]).map((value) => (
                                              <Chip key={value} label={value} />
                                          ))}
                                      </Box>
                                  )
                                : undefined
                        }
                    >
                        {options.map((option, index) => (
                            <MenuItem key={index} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        />
    );
};

export default ETTSelectComponent;
